<template>
  <div>
    <!-- Loader -->
    <div v-if="loading" class="d-flex justify-center py-10">
      <v-progress-circular indeterminate color="#1b273e" :size="48" />
    </div>

    <!-- Empty -->
    <div v-else-if="!orders.length" class="mpb-empty">
      <v-icon size="56" color="#b0bec5">mdi-package-variant-closed</v-icon>
      <p class="mpb-empty__text">Aún no tienes compras de productos o servicios.</p>
    </div>

    <!-- List -->
    <div v-else class="mpb-list">
      <div
        v-for="(order, idx) in orders"
        :key="order._id"
        class="mpb-list__item"
        :class="{ 'mpb-list__item--last': idx === orders.length - 1 }"
        @click="goToMicrosite(order)"
      >
        <!-- Thumbnail -->
        <div class="mpb-list__thumb">
          <img
            v-if="order.productService && order.productService.bannerImage"
            :src="order.productService.bannerImage"
            class="mpb-list__thumb-img"
            :alt="order.productService.name"
          />
          <v-icon v-else size="28" color="#b0bec5">mdi-package-variant</v-icon>
        </div>

        <!-- Info -->
        <div class="mpb-list__info">
          <div class="mpb-list__name">
            {{ order.productService ? order.productService.name : "Producto" }}
          </div>
          <div v-if="order.productService && order.productService.shortDescription" class="mpb-list__desc">
            {{ order.productService.shortDescription }}
          </div>
          <div class="mpb-list__meta">
            {{ formatDate(order.createdAt) }}
          </div>
        </div>

        <!-- Right side -->
        <div class="mpb-list__right">
          <div class="mpb-list__amount">
            {{ formatCurrency(order.total, order.productService && order.productService.currency) }}
          </div>
          <v-chip
            :color="statusColor(order.status)"
            text-color="white"
            x-small
            class="mpb-list__chip font-weight-bold mt-1"
          >
            <v-icon left x-small>{{ statusIcon(order.status) }}</v-icon>
            {{ statusLabel(order.status) }}
          </v-chip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations } from "vuex";

export default {
  name: "MemberPurchasedProductsBlock",
  data() {
    return {
      loading: false,
      orders: [],
    };
  },
  async mounted() {
    this.loading = true;
    const res = await this.fetchMemberProductOrders();
    this.loading = false;
    if (res.status) {
      this.orders = res.data.orders || [];
    } else {
      this.show({ text: res.message || "Error al cargar tus compras.", color: "error" });
    }
  },
  methods: {
    ...mapActions("productServices", ["fetchMemberProductOrders"]),
    ...mapMutations("notification", ["show"]),
    goToMicrosite(order) {
      if (!order.productService || !order.productService._id) return;
      this.$router
        .push({
          name: "productServiceMicrositeById",
          params: { productServiceId: order.productService._id },
        })
        .catch(() => {});
    },
    statusLabel(s) {
      return { COMPLETED: "Pagado", DELIVERED: "Entregado" }[s] || s;
    },
    statusColor(s) {
      return { COMPLETED: "success", DELIVERED: "#1b273e" }[s] || "grey";
    },
    statusIcon(s) {
      return { COMPLETED: "mdi-check-circle-outline", DELIVERED: "mdi-package-variant-closed-check" }[s] || "mdi-information-outline";
    },
    formatDate(d) {
      if (!d) return "";
      return new Date(d).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric" });
    },
    formatCurrency(v, c) {
      return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: c || "MXN",
        minimumFractionDigits: 2,
      }).format(Number(v || 0));
    },
  },
};
</script>

<style scoped>
.mpb-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 0;
  gap: 12px;
}
.mpb-empty__text {
  color: #8e9eb5;
  font-size: 0.95rem;
  margin: 0;
}

/* List */
.mpb-list {
  border: 1px solid #e8ecf2;
  border-radius: 10px;
  overflow: hidden;
}
.mpb-list__item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-bottom: 1px solid #e8ecf2;
  cursor: pointer;
  transition: background 0.15s;
}
.mpb-list__item:hover {
  background: #f7f9fc;
}
.mpb-list__item--last {
  border-bottom: none;
}
.mpb-list__thumb {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  overflow: hidden;
  background: #f0f3f8;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mpb-list__thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.mpb-list__info {
  flex: 1;
  min-width: 0;
}
.mpb-list__name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #1b273e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mpb-list__desc {
  font-size: 0.77rem;
  color: #8e9eb5;
  margin-top: 3px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.mpb-list__meta {
  font-size: 0.74rem;
  color: #b0bec5;
  margin-top: 4px;
}
.mpb-list__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  padding-top: 2px;
}
.mpb-list__amount {
  font-weight: 700;
  font-size: 0.9rem;
  color: #1b273e;
  white-space: nowrap;
}
.mpb-list__chip {
  letter-spacing: 0.02em;
}
</style>
